import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Link as LinkIcon, Plus, X, Clock, Users, AlertTriangle, Loader2, Check, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '../store';

const API_URL = process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? ((window.location.hostname === 'devapp.soviewfx.fr' || window.location.hostname === 'devadmin.soviewfx.fr') ? 'https://devhub.soviewfx.fr' : ((window.location.hostname === 'app.soviewfx.fr' || window.location.hostname === 'admin.soviewfx.fr') ? 'https://hub.soviewfx.fr' : '')) : '');

export default function SharesPage() {
  const navigate = useNavigate();
  const { user, token } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [shares, setShares] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ project_id: '', expires_in_days: 7 });
  const [creating, setCreating] = useState(false);
  const [revokingId, setRevokingId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    if (!user) { navigate('/home'); return; }
    fetchShares();
  }, [user]);

  const fetchShares = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/shares`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setShares(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!createForm.project_id) {
      toast.error('Veuillez renseigner un ID de projet');
      return;
    }
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/shares`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(createForm)
      });
      if (!res.ok) throw new Error('Create failed');
      const newShare = await res.json();
      setShares(prev => [newShare, ...prev]);
      setShowCreate(false);
      setCreateForm({ project_id: '', expires_in_days: 7 });
      toast.success('Lien de partage créé !');
    } catch (err) {
      toast.error('Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (shareId) => {
    if (!window.confirm('Révoquer ce lien de partage ? Les accès existants seront désactivés.')) return;
    setRevokingId(shareId);
    try {
      const res = await fetch(`${API_URL}/api/shares/${shareId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Revoke failed');
      setShares(prev => prev.map(s => s.id === shareId ? { ...s, is_revoked: true } : s));
      toast.success('Lien révoqué');
    } catch (err) {
      toast.error('Erreur lors de la révocation');
    } finally {
      setRevokingId(null);
    }
  };

  const handleCopyLink = (share) => {
    const shareUrl = share.url || `${window.location.origin}/share/${share.token}`;
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopiedId(share.id);
      setTimeout(() => setCopiedId(null), 2000);
      toast.success('Lien copié !');
    }).catch(() => toast.error('Erreur de copie'));
  };

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  const getStatusBadge = (share) => {
    if (share.is_revoked) return <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 font-medium">Révoqué</span>;
    if (isExpired(share.expires_at)) return <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400 font-medium">Expiré</span>;
    return <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 font-medium">Actif</span>;
  };

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <header className="h-14 glass flex items-center px-4 gap-4 flex-shrink-0">
        <button onClick={() => navigate('/home')} className="p-2 rounded-lg hover:bg-white/5">
          <ArrowLeft size={20} />
        </button>
        <h1 className="font-display font-bold text-lg uppercase tracking-wide flex-1">
      Liens de partage
        </h1>
        <button
          onClick={() => setShowCreate(true)}
          className="p-2 rounded-lg bg-primary/20 text-primary hover:bg-primary/30 transition-all"
        >
          <Plus size={20} />
        </button>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 pb-safe">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="spinner" />
          </div>
        ) : shares.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
            <LinkIcon size={40} className="mb-2 opacity-30" />
            <p className="text-sm">Aucun lien de partage</p>
            <button
              onClick={() => setShowCreate(true)}
              className="mt-3 btn-secondary text-xs"
            >
              Créer un lien
            </button>
          </div>
        ) : (
          shares.map((share, i) => (
            <motion.div
              key={share.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              className={`card p-4 ${share.is_revoked ? 'opacity-60' : ''}`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <LinkIcon size={16} className={share.is_revoked ? 'text-red-400' : 'text-primary'} />
                  <p className="text-sm font-semibold text-white">
                    {share.project_id ? `Projet #${share.project_id}` : 'Lien de partage'}
                  </p>
                </div>
                {getStatusBadge(share)}
              </div>

              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground mb-3">
                <span className="flex items-center gap-1">
                  <Clock size={12} />
                  Créé le {formatDate(share.created_at)}
                </span>
                {share.expires_at && (
                  <span className={`flex items-center gap-1 ${isExpired(share.expires_at) ? 'text-yellow-400' : ''}`}>
                    <AlertTriangle size={12} />
                    Expire le {formatDate(share.expires_at)}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Users size={12} />
                  {share.access_count ?? 0} accès
                </span>
              </div>

              <div className="flex gap-2 pt-2 border-t border-white/5">
                <button
                  onClick={() => handleCopyLink(share)}
                  className="flex-1 flex items-center justify-center gap-1.5 text-xs font-medium py-2 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-all"
                >
                  {copiedId === share.id ? (
                    <><Check size={14} /> Copié</>
                  ) : (
                    <><Copy size={14} /> Copier le lien</>
                  )}
                </button>
                {!share.is_revoked && (
                  <button
                    onClick={() => handleRevoke(share.id)}
                    disabled={revokingId === share.id}
                    className="flex-1 flex items-center justify-center gap-1.5 text-xs font-medium py-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all disabled:opacity-50"
                  >
                    {revokingId === share.id ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : 'Révoquer'}
                  </button>
                )}
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* Create Modal */}
      {showCreate && (
        <>
          <div className="modal-overlay" onClick={() => setShowCreate(false)} />
          <div className="sheet">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-display font-bold text-lg text-white">Créer un lien</h2>
              <button onClick={() => setShowCreate(false)} className="p-1 rounded-lg hover:bg-white/5">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs text-muted-foreground mb-1.5">ID du projet</label>
                <input
                  type="text"
                  value={createForm.project_id}
                  onChange={(e) => setCreateForm(f => ({ ...f, project_id: e.target.value }))}
                  className="input"
                  placeholder="ex: abc-123"
                />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1.5">Expiration (jours)</label>
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={createForm.expires_in_days}
                  onChange={(e) => setCreateForm(f => ({ ...f, expires_in_days: Number(e.target.value) }))}
                  className="input"
                />
              </div>
              <button
                onClick={handleCreate}
                disabled={creating}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                {creating ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
                Créer le lien
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
